/**
 * D-3 会话历史 —— `/api/conversations`（契约 §7 D-3，2026-09-03 已建成）。
 *
 * 沿用 `http.ts` 那一套（`x-trace-id`、401 单飞刷新都在里面），本层只拼路径与 schema。
 *
 * ★ 隐私纪律：会话 `title` 是**用户第一条消息的原文**，消息 `content` 是对话正文 ——
 *   本层**不缓存、不打日志**，调用方也别把它们塞进 URL、埋点或 Sentry。
 * ⚠ 全部端点都要登录（Controller 在出口边把缺身份拦成 401 `AUTH.LOGIN_REQUIRED`），
 *   所以用默认 `auth: 'required'`。
 */
import {
  CONVERSATION_MAX_PAGE_SIZE,
  conversationSummarySchema,
  historyMessageListSchema,
  pageResultSchema,
  voidDataSchema,
  type ConversationSummary,
  type HistoryMessage,
  type PageResult,
} from '@cione/contracts'
import type { HttpClient } from './http'

const conversationPageSchema = pageResultSchema(conversationSummarySchema)

/**
 * 把页大小夹进后端认的区间。
 *
 * ⚠ 后端 `Math.min(size, MAX_PAGE_SIZE)` 是**静默**截断：发 500 拿回 100 条不报错，
 *   界面却按 500 判断「是不是到头了」，于是永远以为还有下一页。在客户端就夹住，
 *   比在四个调用点各自记得这条规则可靠。
 */
function clampSize(size: number | undefined): number | undefined {
  if (size === undefined) return undefined
  return Math.min(Math.max(1, Math.trunc(size)), CONVERSATION_MAX_PAGE_SIZE)
}

export interface ConversationsApi {
  /** 我的会话列表，按最后活跃时间倒序。⚠ `page` 从 1 开始（后端 `Math.max(page, 1)`）。 */
  list(params?: { page?: number; size?: number }): Promise<PageResult<ConversationSummary>>
  /**
   * 一个会话的消息 —— **游标**分页，往前翻历史。
   *
   * ⚠⚠ 怎么翻：第一次**不带** `before`，拿到最新的一页；之后每次把
   *   **本页第一条的 `seq`** 传回来。返回恒为 `seq` 升序（旧 → 新）。
   * ⚠⚠ **返回空数组 = 到头了，不是出错** —— 界面要说「没有更早的了」，别报错。
   */
  messages(
    conversationId: string,
    params?: { before?: number; limit?: number },
  ): Promise<HistoryMessage[]>
  /**
   * 删除一个会话（整个会话的消息一起，软删）。
   *
   * ⚠ 幂等：删不到也回 200 —— 「删第二次报会话不存在」只会让用户以为第一次没成功。
   * ⚠⚠ 调用方的责任：**删掉的如果是当前正在用的会话，必须同时换本地 `conversationId`
   *   并清掉本地落盘**。后端删除时会驱逐记忆缓存（否则「用户点了删除、模型下一句话还记得」），
   *   前端这边漏了就是同一个 bug 的另一半：界面还留着已删会话的内容，
   *   接下来发的消息还往一个服务端已经删掉的 id 上追加。见 `stores/chat-store.ts`。
   */
  remove(conversationId: string): Promise<void>
}

export function createConversationsApi(http: HttpClient): ConversationsApi {
  return {
    list(params) {
      return http.request({
        method: 'GET',
        path: '/api/conversations',
        query: { page: params?.page, size: clampSize(params?.size) },
        schema: conversationPageSchema,
      })
    },

    messages(conversationId, params) {
      return http.request({
        method: 'GET',
        path: `/api/conversations/${encodeURIComponent(conversationId)}/messages`,
        // ⚠ `before` 不传 = 从最新一条开始（后端 `before == null || <= 0` 都是这个语义）
        query: { before: params?.before, limit: clampSize(params?.limit) },
        schema: historyMessageListSchema,
      })
    },

    async remove(conversationId) {
      await http.request({
        method: 'DELETE',
        path: `/api/conversations/${encodeURIComponent(conversationId)}`,
        schema: voidDataSchema,
      })
    },
  }
}
